import {
  PenTool, Brain, BrainCircuit, Sparkles, Zap, Wand2, Lightbulb, Cpu,
  Bot, Atom, Activity, Rocket, Target, Telescope, Flame, ScrollText,
  Compass, Hourglass, Eye, Gauge, Crown, MessageSquare, Feather, Star,
  Plus, Trash2, type LucideIcon,
} from 'lucide-react';
import {
  MODELS,
  PROVIDER_LABELS,
  PROVIDERS,
  ICON_CHOICES,
  effortChoicesFor,
  clampEffort,
  generateModeId,
  blankSlot,
  slotsForMode,
  type Mode,
  type ModeSlot,
  type Provider,
} from '@/lib/modes';
import { Input, Textarea, Select, Label } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

// Explicit map (tree-shakeable, matches the codebase's named-import style).
// Keep keys in sync with ICON_CHOICES in lib/modes.ts.
const ICON_MAP: Record<string, LucideIcon> = {
  PenTool, Brain, BrainCircuit, Sparkles, Zap, Wand2, Lightbulb, Cpu,
  Bot, Atom, Activity, Rocket, Target, Telescope, Flame, ScrollText,
  Compass, Hourglass, Eye, Gauge, Crown, MessageSquare, Feather, Star,
};

const NEW_MODE: Omit<Mode, 'id'> = {
  title: '',
  description: '',
  icon: 'Sparkles',
  maxTurns: 150,
  models: {
    openai: blankSlot('openai'),
    claude: blankSlot('claude'),
  },
};

/**
 * Read-only summary of a modes list — one friendly line per mode (icon, title,
 * each provider's model + thinking, turns) instead of a raw JSON blob. Used
 * where modes are displayed but not edited (e.g. the tenant detail settings
 * table). Tolerates legacy single-model modes via slotsForMode.
 */
export function ModesSummary({ modes }: { modes: Mode[] }) {
  if (!modes.length) return <span className="text-xs text-slate-400">No modes defined.</span>;
  return (
    <ul className="space-y-1.5">
      {modes.map((mode, i) => {
        const Icon = ICON_MAP[mode.icon] ?? Sparkles;
        const slots = slotsForMode(mode);
        return (
          <li key={mode.id || i} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-slate-200 bg-white text-slate-600">
              <Icon className="h-3.5 w-3.5" />
            </span>
            <span className="font-medium text-slate-800">{mode.title || '(untitled)'}</span>
            {PROVIDERS.map((p) => {
              const slot = slots[p];
              const model = MODELS.find((m) => m.id === slot.model);
              const thinking = slot.thinking?.enabled ? `thinking ${slot.thinking.effort}` : 'no thinking';
              return (
                <span key={p} className="text-xs text-slate-500">
                  {PROVIDER_LABELS[p] ?? p}: {model?.label ?? slot.model}
                  <span className="text-slate-400"> ({thinking})</span>
                </span>
              );
            })}
            <span className="text-xs text-slate-400">· {mode.maxTurns} turns</span>
          </li>
        );
      })}
    </ul>
  );
}

export function ModesEditor({
  value,
  onChange,
}: {
  value: Mode[];
  onChange: (modes: Mode[]) => void;
}) {
  function update(index: number, patch: Partial<Mode>) {
    onChange(value.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  }

  function remove(index: number) {
    if (value.length <= 1) return;
    onChange(value.filter((_, i) => i !== index));
  }

  function add() {
    onChange([...value, { id: generateModeId(), ...NEW_MODE }]);
  }

  return (
    <div className="space-y-3">
      {value.map((mode, i) => (
        <ModeCard
          key={mode.id || i}
          mode={mode}
          canRemove={value.length > 1}
          onChange={(patch) => update(i, patch)}
          onRemove={() => remove(i)}
        />
      ))}
      <Button variant="outline" size="sm" type="button" onClick={add}>
        <Plus className="h-4 w-4" /> Add another mode
      </Button>
    </div>
  );
}

// One provider's model + thinking controls. Switching the model can move
// between effort tier sets — clamp the effort to the new model's set.
function ProviderSlotEditor({
  provider,
  slot,
  onChange,
}: {
  provider: Provider;
  slot: ModeSlot;
  onChange: (slot: ModeSlot) => void;
}) {
  const models = MODELS.filter((m) => m.provider === provider);
  const enabled = !!slot.thinking?.enabled;
  const effortChoices = effortChoicesFor(slot.model);
  const effort = clampEffort(slot.thinking?.effort ?? 'medium', slot.model);

  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        {PROVIDER_LABELS[provider] ?? provider}
      </div>
      <div>
        <Label>Model</Label>
        <Select
          value={slot.model}
          onChange={(e) => onChange({ model: e.target.value, thinking: { enabled, effort: clampEffort(effort, e.target.value) } })}
        >
          {/* A saved id that's no longer in the registry (e.g. a renamed
              model) must stay visible — a controlled select with an unmatched
              value otherwise renders blank/first-option while the stale id is
              what would be saved. */}
          {!models.some((m) => m.id === slot.model) && (
            <option value={slot.model}>{slot.model} — not in registry</option>
          )}
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </Select>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <Label>Adaptive thinking</Label>
          <Select
            value={enabled ? 'true' : 'false'}
            onChange={(e) => onChange({ model: slot.model, thinking: { enabled: e.target.value === 'true', effort } })}
          >
            <option value="false">Disabled</option>
            <option value="true">Enabled</option>
          </Select>
        </div>
        <div>
          <Label>Thinking effort</Label>
          <Select
            value={effort}
            onChange={(e) => onChange({ model: slot.model, thinking: { enabled, effort: e.target.value } })}
          >
            {effortChoices.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
}

function ModeCard({
  mode,
  canRemove,
  onChange,
  onRemove,
}: {
  mode: Mode;
  canRemove: boolean;
  onChange: (patch: Partial<Mode>) => void;
  onRemove: () => void;
}) {
  const PreviewIcon = ICON_MAP[mode.icon] ?? Sparkles;
  // Render-tolerant: migrate legacy single-model modes into both slots for
  // display. Editing any slot writes back the new `models` shape and clears the
  // legacy fields, so the mode is upgraded on first edit (operators aren't
  // forced to re-save un-edited modes — slotsForMode keeps tolerating them).
  const slots = slotsForMode(mode);
  function patchSlot(provider: Provider, slot: ModeSlot) {
    onChange({ models: { ...slots, [provider]: slot }, model: undefined, thinking: undefined });
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
      <div className="flex items-center gap-2">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600">
          <PreviewIcon className="h-4 w-4" />
        </span>
        <Input
          value={mode.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Mode title (e.g. Standard)"
          className="flex-1"
        />
        <Button
          variant="ghost"
          size="sm"
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          title={canRemove ? 'Remove this mode' : 'At least one mode is required'}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label>Description</Label>
          <Textarea
            rows={2}
            value={mode.description}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Short helper text shown in the dropdown"
          />
        </div>

        <div>
          <Label>Icon</Label>
          <Select value={mode.icon} onChange={(e) => onChange({ icon: e.target.value })}>
            {ICON_CHOICES.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Max turns</Label>
          <Input
            type="number"
            min={1}
            value={mode.maxTurns}
            onChange={(e) => onChange({ maxTurns: parseInt(e.target.value, 10) || 1 })}
          />
        </div>
      </div>

      {/* One model + thinking config per provider, so the same mode runs on
          whichever provider a chat locks to. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {PROVIDERS.map((p) => (
          <ProviderSlotEditor key={p} provider={p} slot={slots[p]} onChange={(slot) => patchSlot(p, slot)} />
        ))}
      </div>
    </div>
  );
}
